import React, { CSSProperties, useState } from 'react';
import { ApprovalStatus, getStatusDisplayName, Preset } from '../../main/components/Preset/models';
import { environment } from '../../../Config';
import { toast } from 'react-toastify';
import { useAuthStore } from '../../../features/authentication/stores/authStore';
import RejectPresetModal from '../../preset/components/RejectModal';
import { prepareSql } from '../../../utils';
import { Role } from '../../../features/authentication';

// Reusable style objects to eliminate duplication
const baseButtonStyle: CSSProperties = {
  padding: '1.4vh 1.8vh',
  cursor: 'pointer',
  fontSize: '1.4vh',
  fontWeight: '500',
  backgroundColor: '#ffffff',
  transition: 'all 0.2s ease',
  borderRadius: '0.8vh',
};

// Color variants matching the app's design system
const buttonVariants = {
  approve: {
    ...baseButtonStyle,
    color: '#00AB9B',
    backgroundColor: 'rgba(0, 171, 155, 0.05)',
  },
  reject: {
    ...baseButtonStyle,
    color: '#F37766',
    backgroundColor: 'rgba(243, 119, 102, 0.05)',
  },
  action: {
    ...baseButtonStyle,
    color: '#3498DB',
    backgroundColor: 'rgba(4, 129, 200, 0.05)',
  },
  private: {
    ...baseButtonStyle,
    color: 'rgba(16, 29, 59, 0.4)',
    backgroundColor: 'rgba(16, 29, 59, 0.05)',
  },
};

// Reusable button component
interface DropdownButtonProps {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  variant?: 'approve' | 'reject' | 'action' | 'private';
}

const DropdownButton: React.FC<DropdownButtonProps> = ({ children, onClick, variant = 'action' }) => {
  const baseStyle = buttonVariants[variant];

  const getHoverColors = (variant: string) => {
    switch (variant) {
      case 'approve':
        return {
          backgroundColor: 'rgba(0, 171, 155, 0.1)', // Light teal
          color: '#009688', // Darker teal
        };
      case 'reject':
        return {
          backgroundColor: 'rgba(243, 119, 102, 0.1)', // Light coral
          color: '#E55A4A', // Darker coral
        };
      case 'action':
        return {
          backgroundColor: 'rgba(52, 152, 219, 0.1)', // Light blue
          color: '#2980B9', // Darker blue (matches hover in SavePresetButton)
        };
      case 'private':
      default:
        return {
          backgroundColor: 'rgba(16, 29, 59, 0.1)', // Light gray
          color: 'rgba(16, 29, 59, 0.4)',
        };
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const hoverColors = getHoverColors(variant);
    e.currentTarget.style.backgroundColor = hoverColors.backgroundColor;
    e.currentTarget.style.color = hoverColors.color;
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.backgroundColor = baseStyle.backgroundColor as string;
    e.currentTarget.style.color = baseStyle.color as string;
  };

  return (
    <div style={baseStyle} onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
    </div>
  );
};

function StatusDropdown({
  preset,
  setStatusDropdownOpen,
  fetchPresets,
}: {
  preset: Preset;
  setStatusDropdownOpen: (statusDropdownOpen: number | null) => void;
  fetchPresets: () => void;
}) {
  const { canApproveRejectApprover, canApproveRejectCRO, getRole } = useAuthStore();

  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const handleChangeStatusRequest = async (preset: Preset, status: ApprovalStatus, reason?: string) => {
    try {
      const response = await fetch(
        `https://prod-15.eastus.logic.azure.com:443/workflows/935c0d0a0c654359b40a954cc118770b/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=BzAcr3yCy2ijN2IrSloKghn27qZenIWePKTcWkyZwzA`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: preset.id,
            newStatus: status,
            rejectionReason: reason ? prepareSql(reason) : null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to change status');
      }

      fetchPresets();
    } catch (error) {
      console.error('Error approving preset:', error);
    }
  };

  const notifySales = async (preset: Preset, status: ApprovalStatus) => {
    try {
      const response = await fetch(
        'https://prod-63.eastus.logic.azure.com:443/workflows/fb031a87ddcc4fee89a2a78a306662d9/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=sqmR4K3nazu8K5dwq5gj4dYQ9nVGJUp-BRMkurGx6w0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: preset.id,
            email: preset.user,
            label: preset.name,
            status: getStatusDisplayName(status, preset.overallDiscount < 0),
          }),
        }
      );
    } catch (error) {
      console.error('Error notifying email:', error);
    }
  };

  const notifyCRO = async (preset: Preset) => {
    try {
      const response = await fetch(
        environment === 'dev'
          ? `https://prod-49.eastus.logic.azure.com:443/workflows/500d66dea1e14e34ad6475bdba3e8524/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=hEzrJ8q_HIzncEXByM5t9PFlbusdvMfxk71qc4fpXzE`
          : `https://prod-72.eastus.logic.azure.com:443/workflows/0dcefd14b0aa4f12abfae1def3573c8f/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=S73oQWTY6n4K7NXwFTYi3FQyeknkMSR8pOhOJ-1aAUE`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: preset.id,
            email: preset.user,
            label: preset.name,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve preset');
      }

      toast('Preset was sent on review', {
        position: 'top-right',
        autoClose: 2000,
        theme: 'colored',
        style: {
          color: 'rgba(16, 29, 59, 1)',
          fontFamily: 'SF Pro Display',
          fontWeight: '500',
        },
        progressStyle: {
          background: '#00AB9B',
        },
      });
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const notifyApproverAfterCro = async (preset: Preset) => {
    try {
      const response = await fetch(
        environment === 'dev'
          ? `https://prod-49.eastus.logic.azure.com:443/workflows/500d66dea1e14e34ad6475bdba3e8524/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=hEzrJ8q_HIzncEXByM5t9PFlbusdvMfxk71qc4fpXzE`
          : `https://prod-51.eastus.logic.azure.com:443/workflows/9ed1fdf53189453384e673260103bc0b/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=-d19FgtcTxJDHjC0f2Wt5vveWariiMkNSbraTHJm8Gw`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: preset.id,
            email: preset.user,
            label: preset.name,
          }),
        }
      );
    } catch (error) {
      console.error('Error notifying approval:', error);
    }
  };

  const onStatusChange = (preset: Preset, status: ApprovalStatus, reason?: string) => {
    handleChangeStatusRequest(preset, status, reason);

    if (status === ApprovalStatus.PENDING) {
      notifyCRO(preset);
    } else if (status === ApprovalStatus.APPROVED_CRO && preset.overallDiscount < 0) {
      notifyApproverAfterCro(preset);
    }

    notifySales(preset, status);

    setStatusDropdownOpen(null);
  };

  const getRejectStatus = (): ApprovalStatus => {
    if (getRole() === Role.CRO) {
      return ApprovalStatus.REJECTED_CRO;
    } else if (getRole() === Role.ADMIN) {
      return ApprovalStatus.REJECTED_APPROVER;
    }

    return preset.approvalStatus;
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        padding: '1.6vh',
        right: 0,
        backgroundColor: '#ffffff',
        border: 'none',
        borderRadius: '1.6vh',
        boxShadow: '0vh 0.8vh 2.4vh -0.4vh rgba(16, 29, 59, 0.15)',
        zIndex: 2000,
        minWidth: '42.7vh',
        overflow: 'hidden',
        boxSizing: 'border-box' as 'border-box',
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '1.2vh',
      }}
    >
      {rejectModalOpen && (
        <RejectPresetModal
          onRequestClose={() => setRejectModalOpen(false)}
          onSubmit={reason => {
            onStatusChange(preset, getRejectStatus(), reason);
          }}
        />
      )}
      {preset.approvalStatus === ApprovalStatus.PRIVATE && (
        <DropdownButton
          variant="action"
          onClick={e => {
            e.stopPropagation();
            onStatusChange(preset, ApprovalStatus.PENDING);
          }}
        >
          Send for Review
        </DropdownButton>
      )}
      {canApproveRejectApprover(preset) && (
        <>
          <DropdownButton
            variant="approve"
            onClick={e => {
              e.stopPropagation();
              onStatusChange(preset, ApprovalStatus.APPROVED_APPROVER);
            }}
          >
            Approve
          </DropdownButton>
          <DropdownButton
            variant="reject"
            onClick={e => {
              e.stopPropagation();
              setRejectModalOpen(true);
            }}
          >
            Reject
          </DropdownButton>
        </>
      )}
      {canApproveRejectCRO(preset) && (
        <>
          <DropdownButton
            variant="approve"
            onClick={e => {
              e.stopPropagation();
              onStatusChange(preset, ApprovalStatus.APPROVED_CRO);
            }}
          >
            Approve
          </DropdownButton>
          <DropdownButton
            variant="reject"
            onClick={e => {
              e.stopPropagation();
              setRejectModalOpen(true);
            }}
          >
            Reject
          </DropdownButton>
        </>
      )}
      {preset.approvalStatus !== ApprovalStatus.PRIVATE && (
        <DropdownButton
          variant="private"
          onClick={e => {
            e.stopPropagation();
            onStatusChange(preset, ApprovalStatus.PRIVATE);
          }}
        >
          Draft
        </DropdownButton>
      )}
    </div>
  );
}

export default StatusDropdown;
